import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { formatDistanceToNow, isBefore } from 'date-fns';
import OpportunityFollowUpSchedulePanel, {
  followUpInHours,
} from './OpportunityFollowUpSchedulePanel';

function formatFollowUpDisplay(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatDueInLabel(iso: string, now: Date): string {
  const due = new Date(iso);
  if (Number.isNaN(due.getTime())) return '';
  const distance = formatDistanceToNow(due);
  if (isBefore(due, now)) {
    return `Overdue by ${distance}`;
  }
  return `Due in ${distance}`;
}

interface OpportunityNextFollowUpHeaderProps {
  nextFollowUpAt: string | null;
  onSave: (followUpAt: string | null) => Promise<void>;
  onComplete?: () => Promise<void>;
  disabled?: boolean;
  /**
   * Tightens spacing for embedding inside a compact surface (e.g. the
   * conversation Lead banner) by dropping the heavy top divider/margin.
   */
  compact?: boolean;
}

export default function OpportunityNextFollowUpHeader({
  nextFollowUpAt,
  onSave,
  onComplete,
  disabled = false,
  compact = false,
}: OpportunityNextFollowUpHeaderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [customDraft, setCustomDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    if (!nextFollowUpAt) return;
    setNow(new Date());
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, [nextFollowUpAt]);

  useEffect(() => {
    if (!menuOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setMenuOpen(false);
        setCustomDraft('');
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [menuOpen]);

  const openMenu = () => {
    if (disabled) return;
    setCustomDraft('');
    setMenuOpen(true);
  };

  const saveFollowUp = async (iso: string) => {
    setSaving(true);
    try {
      await onSave(iso);
      setMenuOpen(false);
      setCustomDraft('');
    } finally {
      setSaving(false);
    }
  };

  const handleQuickSelect = (hours: number) => {
    if (saving) return;
    void saveFollowUp(followUpInHours(hours));
  };

  const handleCustomCommit = (value: string) => {
    if (!value.trim() || saving) return;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return;
    void saveFollowUp(parsed.toISOString());
  };

  const handleComplete = async () => {
    if (!onComplete || saving || disabled) return;
    setSaving(true);
    try {
      await onComplete();
      setMenuOpen(false);
      setCustomDraft('');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`w-full min-w-0 ${
        compact ? 'mt-0' : 'mt-2.5 pt-2.5 border-t border-gray-200/60'
      }`}
    >
      <div
        className={
          compact
            ? 'flex w-full min-w-0 items-center justify-center min-h-[28px]'
            : 'grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-2'
        }
      >
        {/* compact mode drops the label — the capsule copy speaks for itself */}
        {!compact && (
          <p className="min-w-0 truncate text-[10px] font-semibold uppercase tracking-wide text-gray-400">
            Next Follow up
          </p>
        )}
        <div ref={rootRef} className={`relative flex min-w-0 flex-col ${compact ? 'items-center' : 'items-end justify-self-end'}`}>
          <div className="flex max-w-full items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={openMenu}
              disabled={disabled || saving}
              aria-expanded={menuOpen}
              aria-haspopup="dialog"
              className={
                compact
                  ? `inline-flex min-h-[28px] items-center touch-manipulation whitespace-nowrap rounded-full transition-all duration-150 active:scale-[0.96] disabled:opacity-50 ${
                      nextFollowUpAt
                        ? 'max-w-[min(100%,12rem)] truncate bg-black/[0.04] px-3 py-1 text-[12px] font-semibold text-gray-700 active:bg-black/[0.08]'
                        : 'bg-blue-500/10 px-3 py-1 text-[12px] font-semibold text-blue-600 active:bg-blue-500/20'
                    }`
                  : `text-[12px] font-semibold text-blue-600 underline touch-manipulation whitespace-nowrap rounded-md py-0.5 hover:text-blue-800 active:scale-[0.98] transition-all disabled:opacity-50 ${
                      nextFollowUpAt ? 'max-w-[min(100%,12rem)] truncate text-right' : ''
                    }`
              }
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {nextFollowUpAt ? formatFollowUpDisplay(nextFollowUpAt) : compact ? '+ Set follow-up' : 'None'}
            </button>
            {nextFollowUpAt && onComplete ? (
              <button
                type="button"
                onClick={() => void handleComplete()}
                disabled={disabled || saving}
                aria-label="Mark follow-up complete"
                title="Mark follow-up complete"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-emerald-200 bg-emerald-50 text-emerald-600 hover:bg-emerald-100 active:scale-95 transition-all disabled:opacity-50 touch-manipulation"
                style={{ WebkitTapHighlightColor: 'transparent' }}
              >
                <Check className="h-3.5 w-3.5 stroke-[2.5]" />
              </button>
            ) : null}
          </div>
          {nextFollowUpAt ? (
            <p
              className={`mt-0.5 max-w-[min(100%,12rem)] truncate text-right ${compact ? 'text-[11px]' : 'text-[10px]'} ${
                isBefore(new Date(nextFollowUpAt), now) ? 'text-red-500 font-medium' : 'text-gray-400'
              }`}
            >
              {formatDueInLabel(nextFollowUpAt, now)}
            </p>
          ) : null}

          {menuOpen ? (
            <div
              role="dialog"
              aria-label="Follow up in"
              className="absolute right-0 top-full z-[30] mt-2 w-[min(100vw-2rem,300px)] rounded-xl border border-gray-200/90 bg-white p-3 shadow-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <OpportunityFollowUpSchedulePanel
                customDraft={customDraft}
                saving={saving}
                onCustomDraftChange={setCustomDraft}
                onQuickSelect={handleQuickSelect}
                onCustomCommit={handleCustomCommit}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
